import { useMutation, useQueryClient } from "@tanstack/react-query";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAutoClearState } from "../../../../common/hooks/useAutoClearState";
import { useModals } from "../../../../common/hooks/useModals";
import { blockUser, leaveChatRoom, reportUser, unblockUser } from "../../apis/chatApi";
import { SCREENS } from "../../constants/constants";
import { useBlockStatus } from "../../hooks/useBlockStatus";
import { blockedUsersQueryKey, useChatBlockUsersLoad } from "../../hooks/useChatBlockUsersLoad";
import { useChatRoom } from "../../hooks/useChatRoom";
import { chatRoomsQueryKey, useChatRoomsLoad } from "../../hooks/useChatRoomsLoad";
import { useRoomDisplayMeta } from "../../hooks/useRoomDisplayMeta";
import { setMessengerVisible } from "../../stores/messengerOpenRoomStore";
import { requestChatTotalUnreadRefresh } from "../../websocket/webSocketConnection";
import BlockedUsersPage from "../ChatList/BlockedUsersPage/BlockedUsersPage";
import ChatRoomListPage from "../ChatList/ChatRoomListPage/ChatRoomListPage";
import ChatRoomPage from "../ChatRoom/ChatRoomPage";
import ChatSettingsPage from "../ChatSetting/ChatSettingsPage";
import ChatManageModal from "../shared/ChatManageModal/ChatManageModal";
import ConfirmModal from "../shared/ConfirmModal/ConfirmModal";
import styles from "./ChatPage.module.scss";

/**
 * 채팅 통합 페이지 — 모든 유스케이스를 한 페이지에서 처리
 * - 기본: 채팅방 목록 (삭제 모드, 설정 버튼)
 * - 채팅방 클릭 → 대화 상세
 * - 설정 → 설정 메뉴 → 신고/차단/알림/나가기 서브
 */
function ChatPage({
  className = "",
  initialScreen,
  initialSelectedRoom,
  onCloseClick,
  onLeaveRooms,
  onRegisterBackHandler,
  suid,
  roomId,
}) {
  // --- 화면 네비게이션 ---
  const [screen, setScreen] = useState(initialScreen ?? SCREENS.LIST);
  const [previousScreen, setPreviousScreen] = useState(SCREENS.LIST);
  const [selectedRoom, setSelectedRoom] = useState(initialSelectedRoom ?? null);
  const [deleteMode, setDeleteMode] = useState(false);
  const chatRoomWrapperRef = useRef(null);
  const chatRoomTransitionTimer = useRef(null);
  const onCloseClickRef = useRef(onCloseClick);
  onCloseClickRef.current = onCloseClick;
  const openedAsSettings = initialScreen === SCREENS.SETTINGS;
  const qc = useQueryClient();
  // suid: 매물 문의하기로 진입 시 첫 메시지에만 첨부. 채팅방에서 나오면 초기화 (미전송 시 소멸)
  const [activeSuid, setActiveSuid] = useState(suid ?? null);

  const [enteringChatRoom, setEnteringChatRoom] = useState(false);

  useEffect(() => {
    setMessengerVisible(true);
    return () => setMessengerVisible(false);
  }, []);

  const goToChatRoom = useCallback((room) => {
    clearTimeout(chatRoomTransitionTimer.current);
    setSelectedRoom(room);
    setScreen(SCREENS.CHAT_ROOM);
    if (window.innerWidth <= 768) {
      setEnteringChatRoom(true);
      chatRoomTransitionTimer.current = setTimeout(() => {
        setEnteringChatRoom(false);
      }, 150);
    }
  }, []);

  // roomId prop으로 진입 시: rooms 로드 후 해당 방 자동 선택
  const { data: loadedRooms } = useChatRoomsLoad();
  const roomIdHandled = useRef(false);
  useEffect(() => {
    if (!roomId || roomIdHandled.current || !loadedRooms?.length) return;
    const found = loadedRooms.find((r) => r.id === roomId);
    if (found) {
      roomIdHandled.current = true;
      goToChatRoom(found);
    }
  }, [roomId, loadedRooms, goToChatRoom]);

  const goToSettings = useCallback(() => {
    setPreviousScreen(screen);
    setScreen(SCREENS.SETTINGS);
  }, [screen]);

  const goBack = useCallback(() => {
    setScreen(previousScreen);
  }, [previousScreen]);

  const handleOpenBlockedUsers = useCallback(() => {
    qc.invalidateQueries({ queryKey: blockedUsersQueryKey });
    setScreen(SCREENS.BLOCKED);
  }, [qc]);

  const [leavingChatRoom, setLeavingChatRoom] = useState(false);

  const handleBackToList = useCallback(() => {
    const roomId = selectedRoom?.id;
    const isMobile = window.innerWidth <= 768;
    clearTimeout(chatRoomTransitionTimer.current);
    if (isMobile) {
      setLeavingChatRoom(true);
      chatRoomWrapperRef.current?.classList.add(styles.chatRoomLeave);
    }
    const doSwitch = () => {
      if (roomId) {
        qc.setQueryData(chatRoomsQueryKey, (prev) =>
          (prev || []).map((r) => (r.id === roomId ? { ...r, unreadCount: 0 } : r))
        );
      }
      // 채팅방에서 차단 변화가 일어났을 수 있으므로 목록 배지 동기화
      qc.invalidateQueries({ queryKey: blockedUsersQueryKey });
      setSelectedRoom(null);
      setScreen(SCREENS.LIST);
      setActiveSuid(null);
      setLeavingChatRoom(false);
      requestChatTotalUnreadRefresh();
    };
    if (isMobile) {
      chatRoomTransitionTimer.current = setTimeout(doSwitch, 150);
    } else {
      doSwitch();
    }
  }, [selectedRoom?.id, qc]);

  const handleForcedLeave = useCallback(
    (roomId) => {
      qc.setQueryData(chatRoomsQueryKey, (prev) => (prev || []).filter((r) => r.id !== roomId));
      if (selectedRoom?.id === roomId) {
        setSelectedRoom(null);
        setScreen(SCREENS.LIST);
      }
      onLeaveRooms?.([roomId]);
      requestChatTotalUnreadRefresh();
    },
    [qc, onLeaveRooms, selectedRoom?.id]
  );

  // --- 채팅방 메시지 ---
  const {
    messages: currentRoomMessages,
    hasMore: currentRoomHasMore,
    historyLoading,
    handleLoadMore: handleLoadMoreHistory,
    handleSendMessage,
    handleRetryFailedMessage,
    handleRemoveFailedMessage,
  } = useChatRoom(selectedRoom, screen, { setSelectedRoom, onForcedLeave: handleForcedLeave });

  // --- 방 표시 정보 ---
  const roomDisplay = useRoomDisplayMeta(selectedRoom);

  const chatRoomOtherUserId =
    screen === SCREENS.CHAT_ROOM && selectedRoom
      ? (selectedRoom.members?.find((m) => m.userId && m.userId !== window.loggedInUser?.uuid)
          ?.userId ?? "")
      : "";

  // --- 모달 & 토스트 ---
  const [modals, , { open: openModal, close: closeModal }] = useModals({
    more: false,
    report: false,
    block: false,
    leave: false,
    chatManage: false,
  });
  const [pendingFailedMessage, setPendingFailedMessage] = useState(null);
  const [blockedUsers, setBlockedUsers] = useState([]);
  const [unblockToast, setUnblockToast] = useAutoClearState(null, 3000);
  const [reportToast, setReportToast] = useAutoClearState(null, 3000);
  const [notificationToast, setNotificationToast] = useAutoClearState(null, 3000);
  const [leaveToast, setLeaveToast] = useAutoClearState(null, 3000);

  // --- 차단/신고 ---
  useBlockStatus(chatRoomOtherUserId);
  // 메신저 진입 시 차단 사용자 목록을 1회 fetch → 채팅방 목록 배지 lookup 용
  useChatBlockUsersLoad();

  const blockPeerLabel = useMemo(
    () => roomDisplay.displayName || "상대방",
    [roomDisplay.displayName]
  );

  const blockMutation = useMutation({
    mutationFn: (uid) => blockUser(uid),
    onSuccess: (res) => {
      if (res.success) {
        qc.setQueryData(["blockStatus", chatRoomOtherUserId], (prev) => ({
          ...(prev || {}),
          blockedByMe: true,
        }));
        qc.invalidateQueries({ queryKey: blockedUsersQueryKey });
        setReportToast(`${blockPeerLabel}님 차단되었습니다.`);
      } else {
        setReportToast(`${blockPeerLabel}님 차단에 실패했습니다.`);
      }
    },
    onError: (e) => {
      setReportToast(e?.message ?? `${blockPeerLabel}님 차단에 실패했습니다.`);
    },
  });

  const unblockMutation = useMutation({
    mutationFn: (uid) => unblockUser(uid),
    onSuccess: (res) => {
      if (res?.success !== false) {
        qc.setQueryData(["blockStatus", chatRoomOtherUserId], (prev) => ({
          ...(prev || {}),
          blockedByMe: false,
        }));
        qc.invalidateQueries({ queryKey: blockedUsersQueryKey });
        setReportToast(`${blockPeerLabel}님 차단이 해제되었습니다.`);
      } else {
        setReportToast(res?.message ?? `${blockPeerLabel}님 차단 해제에 실패했습니다.`);
      }
    },
    onError: (e) => {
      setReportToast(e?.message ?? `${blockPeerLabel}님 차단 해제에 실패했습니다.`);
    },
  });

  const reportMutation = useMutation({
    mutationFn: ({ email, otherUser, room }) => reportUser(email, otherUser, room),
    onSuccess: () => setReportToast("신고가 완료되었습니다."),
    onError: () => setReportToast("신고에 실패했습니다. 잠시 후 다시 시도해주세요."),
  });

  // --- 채팅방 나가기 ---
  const leaveRoomsMutation = useMutation({
    mutationFn: (ids) => Promise.all(ids.map((id) => leaveChatRoom(id))),
    onSuccess: (_, ids) => {
      qc.setQueryData(chatRoomsQueryKey, (prev) => (prev || []).filter((r) => !ids.includes(r.id)));
      setLeaveToast("채팅방을 나갔습니다.");
      onLeaveRooms?.(ids);
      requestChatTotalUnreadRefresh();
    },
    onError: (err) => {
      setLeaveToast(err.message || "서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요.");
    },
  });

  const handleLeaveRooms = useCallback(
    async (ids) => {
      if (!ids?.length) return false;
      try {
        await leaveRoomsMutation.mutateAsync(ids);
        return true;
      } catch {
        return false;
      }
    },
    [leaveRoomsMutation]
  );

  const handleSaleCardClick = useCallback(
    (suid) => {
      onRegisterBackHandler?.(null);
      onCloseClick?.();
      window.tab_sale_detail_load?.(suid, true, false, true);
    },
    [onCloseClick, onRegisterBackHandler]
  );

  const handleInternalBack = useCallback(() => {
    switch (screen) {
      case SCREENS.CHAT_ROOM:
        handleBackToList();
        break;
      case SCREENS.SETTINGS:
        if (openedAsSettings) {
          onCloseClickRef.current?.();
        } else {
          goBack();
        }
        break;
      case SCREENS.BLOCKED:
        setScreen(SCREENS.SETTINGS);
        break;
      case SCREENS.LIST:
        setDeleteMode(false);
        break;
      default:
        break;
    }
  }, [screen, handleBackToList, goBack]);

  useEffect(() => {
    if (!onRegisterBackHandler) return;
    if (screen === SCREENS.LIST && !deleteMode) {
      onRegisterBackHandler(null);
    } else {
      onRegisterBackHandler((reRegister) => {
        handleInternalBack();
        reRegister();
      });
    }
  }, [screen, deleteMode, onRegisterBackHandler, handleInternalBack]);

  useEffect(() => {
    return () => clearTimeout(chatRoomTransitionTimer.current);
  }, []);

  const chatWidgetContentRef = useRef(null);

  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;

    const handler = () => {
      const el = chatWidgetContentRef.current;
      if (!el) return;
      if (window.innerWidth > 768) {
        el.style.top = "";
        el.style.height = "";
        return;
      }
      el.style.top = `${vv.offsetTop}px`;
      el.style.height = `${vv.height}px`;
    };

    vv.addEventListener("resize", handler);
    vv.addEventListener("scroll", handler);
    return () => {
      vv.removeEventListener("resize", handler);
      vv.removeEventListener("scroll", handler);
    };
  }, []);

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget && e.isTrusted) onCloseClick?.();
  };

  return (
    <>
      <div className={styles.chatWidgetOverlay} onClick={handleOverlayClick} />
      <div
        ref={chatWidgetContentRef}
        className={styles.chatWidgetContent}
        onClick={(e) => e.stopPropagation()}
      >
        {(screen === SCREENS.LIST || leavingChatRoom || enteringChatRoom) && (
          <>
            <ChatRoomListPage
              className={className}
              onSettingClick={goToSettings}
              onCloseClick={onCloseClick}
              onRoomClick={(item) => goToChatRoom(item)}
              onLeaveRooms={handleLeaveRooms}
              deleteMode={deleteMode}
              onSetDeleteMode={setDeleteMode}
              toastMessage={leaveToast}
            />
          </>
        )}
        {screen === SCREENS.CHAT_ROOM && selectedRoom && (
          <>
            <div ref={chatRoomWrapperRef} className={styles.chatRoomWrapper}>
              <ChatRoomPage
                className={className}
                room={selectedRoom}
                suid={activeSuid}
                messages={currentRoomMessages}
                hasMore={currentRoomHasMore}
                historyLoading={historyLoading}
                onBack={handleBackToList}
                onMoreClick={() => openModal("more")}
                isMoreOpen={modals.more}
                onMoreClose={() => closeModal("more")}
                onReport={() => openModal("report")}
                onBlock={() => openModal("block")}
                onUnblock={() => unblockMutation.mutate(chatRoomOtherUserId)}
                onNotification={() => setNotificationToast("채팅방의 알림이 꺼졌습니다.")}
                onLeave={() => openModal("leave")}
                onLoadMore={handleLoadMoreHistory}
                onSendMessage={handleSendMessage}
                onSaleCardClick={handleSaleCardClick}
                onFailedMessageRetry={(msg) => {
                  setPendingFailedMessage(msg);
                  openModal("chatManage");
                }}
                onFailedMessageRemove={(msg) => {
                  setPendingFailedMessage(msg);
                  openModal("chatManage");
                }}
                reportToastMessage={reportToast}
                notificationToastMessage={notificationToast}
              />
            </div>
          </>
        )}
        {screen === SCREENS.SETTINGS && (
          <ChatSettingsPage
            className={className}
            title="설정"
            onBackClick={openedAsSettings ? onCloseClick : goBack}
            onOpenBlockedUsers={handleOpenBlockedUsers}
            toastMessage={notificationToast}
          />
        )}
        {screen === SCREENS.BLOCKED && (
          <BlockedUsersPage
            className={className}
            title="차단한 사용자"
            items={blockedUsers}
            toastMessage={unblockToast}
            onBack={() => setScreen(SCREENS.SETTINGS)}
            setBlockedUserLoads={setBlockedUsers}
            setUnblockToast={setUnblockToast}
          />
        )}

        <ChatManageModal
          open={modals.chatManage}
          onClose={() => {
            closeModal("chatManage");
            setPendingFailedMessage(null);
          }}
          onRetry={() => {
            handleRetryFailedMessage(pendingFailedMessage);
            closeModal("chatManage");
            setPendingFailedMessage(null);
          }}
          onDelete={() => {
            handleRemoveFailedMessage(pendingFailedMessage);
            closeModal("chatManage");
            setPendingFailedMessage(null);
          }}
        />
        <ConfirmModal
          type="report"
          open={modals.report}
          onClose={() => closeModal("report")}
          onConfirm={() => {
            const otherUser = selectedRoom?.members?.find(
              (m) => m.userId !== window.loggedInUser?.uuid
            );
            reportMutation.mutate({
              email: window.loggedInUser?.email,
              otherUser,
              room: selectedRoom,
            });
            closeModal("report");
          }}
        />
        <ConfirmModal
          type="block"
          open={modals.block}
          onClose={() => closeModal("block")}
          onConfirm={() => {
            if (!chatRoomOtherUserId) {
              closeModal("block");
              return;
            }
            blockMutation.mutate(chatRoomOtherUserId, {
              onSettled: () => closeModal("block"),
            });
          }}
          title="이 사용자를 차단하시겠습니까?"
          description="차단하면 해당 사용자로부터 더 이상 메시지를 받지 않게 됩니다."
          confirmLabel="차단하기"
        />
        <ConfirmModal
          type="leave"
          open={modals.leave}
          onClose={() => closeModal("leave")}
          onConfirm={async () => {
            if (selectedRoom?.id) {
              await handleLeaveRooms([selectedRoom.id]);
              setSelectedRoom(null);
              setScreen(SCREENS.LIST);
            }
            closeModal("leave");
          }}
        />
      </div>
    </>
  );
}

ChatPage.SCREENS = SCREENS;
export default ChatPage;
